import {sanitizeHeaderFooterHtml} from '../../../../components/report-workspace/HeaderFooterModal/utils';
import {
  getContentWidth,
  getPageMargin,
  getLineHeight,
  getElementStableKey,
} from './htmlDom.utils';
import {
  applyEditorWidowOrphanVisualControls,
  EDITOR_WO_PAGE_FOOTER_BOOST_ATTR,
  ensureWoAdjustLayer,
  syncWoAdjustDebugRootClass,
} from './widowOrphan';
import {
  PAGE_BREAK_CLASS,
  PAGE_BREAK_BOTTOM_CLASS,
  PAGE_BREAK_PAGE_INDEX_ATTR,
  PAGE_BREAK_TOP_CLASS,
  PAGE_DIVIDER_HEIGHT,
  PAGE_HEIGHT,
  PAGE_INDEX_EPSILON_PX,
  PAGINATION_REFLOW_EPSILON_PX,
  PAGE_STRIDE,
} from './pagination.constants';

/**
 * Pagination engine for editor-side page-break DOM.
 *
 * High-level responsibilities:
 * 1) Measure section content and derive required page count.
 * 2) Resolve max header/footer heights for the section and expose them via CSS vars.
 * 3) Render synthetic page/page-break DOM for editor visualization.
 * 4) Apply widow/orphan visual controls (which may adjust effective boundaries).
 * 5) Re-check post-apply reflow and retry (bounded) when boundaries move.
 *
 * This file intentionally centralizes DOM mutation order; several downstream
 * systems (snapshot producer, print V2 clone, telemetry) rely on this sequence.
 */

export {PAGE_HEIGHT, PAGE_DIVIDER_HEIGHT};
const NO_END_PAGE = 9999; // Special value to indicate "no end page" in overrides

// Global flag to track if we're in a print cycle
let isInPrintCycle = false;
// Cache for measured header/footer heights to avoid repeated DOM measurements
const headerFooterHeightCache = new Map();

// ---------------------------------------------------------------------------
// HEADER/FOOTER RESOLUTION
// ---------------------------------------------------------------------------
// This section resolves which header/footer HTML applies to each page and
// computes section-wide max heights used by CSS page spacing. We support two
// formats during migration:
// 1) legacy document-level page-range entries
// 2) section-based config with per-section overrides.

/**
 * Check whether the document has any non-empty header content.
 * Purity: pure.
 */
export const hasDocumentHeaders = (documentData) => {
  return (
    documentData?.record?.headers &&
    Array.isArray(documentData.record.headers) &&
    documentData.record.headers.length > 0 &&
    documentData.record.headers.some(
      (header) => header.html && header.html.trim()
    )
  );
};

/**
 * Check whether the document has any non-empty footer content.
 * Purity: pure.
 */
export const hasDocumentFooters = (documentData) => {
  return (
    documentData?.record?.footers &&
    Array.isArray(documentData.record.footers) &&
    documentData.record.footers.length > 0 &&
    documentData.record.footers.some(
      (footer) => footer.html && footer.html.trim()
    )
  );
};

/**
 * Check if either headers or footers are configured.
 * Purity: pure.
 */
const hasAnyHeadersOrFooters = (documentData) => {
  return hasDocumentHeaders(documentData) || hasDocumentFooters(documentData);
};

/**
 * Measure rendered header/footer HTML height with memoization.
 * Purity: impure (creates temporary DOM nodes and reads layout).
 */
export const measureHeaderFooterHeight = (html, type = 'header') => {
  if (!html || !html.trim()) return 0;

  const cacheKey = `${type}:${html}`;
  if (headerFooterHeightCache.has(cacheKey)) {
    return headerFooterHeightCache.get(cacheKey);
  }

  const lineHeight = getLineHeight();

  const contentWidth = getContentWidth();
  const temp = document.createElement('div');
  temp.className = 'header-footer-content';
  const padding =
    type === 'footer' ? 'padding-top: 8px;' : 'padding-bottom: 8px;';

  // 14px not 10.5pt — pt values introduce sub-pixel fractions (10.5pt ≈
  // 13.9999px) that compound rounding errors across zoom levels.
  temp.style.cssText = `
    position: absolute;
    visibility: hidden;
    width: ${contentWidth}px;
    font-size: 14px;
    font-family: var(--document-font-family, 'Times New Roman', Times, serif);
    line-height: ${lineHeight};
    ${padding}
    box-sizing: border-box;
  `;
  // Sanitize HTML before DOM injection for defense-in-depth.
  temp.innerHTML = sanitizeHeaderFooterHtml(html);
  document.body.appendChild(temp);
  const measuredImages = Array.from(temp.querySelectorAll('img'));
  const imageReadiness = measuredImages.map((img) => ({
    complete: !!img.complete,
    naturalWidth: Number.isFinite(img.naturalWidth) ? img.naturalWidth : 0,
    hasDataSrc: /^data:image\//i.test(img.getAttribute('src') || ''),
  }));
  const everyImageSettled = imageReadiness.every(
    (imgState) => imgState.complete
  );

  temp.querySelectorAll('p').forEach((p) => {
    p.style.margin = '0';
  });
  // Match actual header/footer layout: headings have margin 0, so measurement must too
  temp.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((h) => {
    h.style.margin = '0';
  });

  const height = temp.offsetHeight;
  document.body.removeChild(temp);
  // Cache once all images are settled (loaded or errored) to reserve stable space.
  if (everyImageSettled) {
    headerFooterHeightCache.set(cacheKey, height);
  } else {
    headerFooterHeightCache.delete(cacheKey);
  }
  return height;
};

/**
 * Reset cached header/footer measurement results.
 * Clear the height cache (call when headers/footers are updated).
 * Purity: impure (mutates module-level cache).
 */
export const clearHeaderFooterHeightCache = () => {
  headerFooterHeightCache.clear();
};

/**
 * Sync body classes for header/footer-enabled styling.
 * Purity: impure (mutates `document.body.classList`).
 */
export const syncHeaderFooterBodyClasses = (documentData) => {
  if (!documentData) return;
  const hasHeaders = hasDocumentHeaders(documentData);
  const hasFooters = hasDocumentFooters(documentData);

  document.body.classList.remove(
    'document-headers-enabled',
    'document-footers-enabled'
  );
  if (hasHeaders) {
    document.body.classList.add('document-headers-enabled');
  }
  if (hasFooters) {
    // Footer mode intentionally enables both classes: current stylesheet rules
    // use the header-enabled body class to normalize first-block spacing while
    // footers are active, then `document-footers-enabled` layers footer styling.
    document.body.classList.add('document-headers-enabled');
    document.body.classList.add('document-footers-enabled');
  }
};

/**
 * Set pagination print-cycle mode used by layout decisions.
 * Purity: impure (mutates module-level state).
 */
export const setPrintCycle = (inPrintCycle) => {
  isInPrintCycle = inPrintCycle;
};

/**
 * Read pagination print-cycle mode.
 * Purity: impure (reads module-level mutable state).
 */
export const getIsInPrintCycle = () => isInPrintCycle;

/**
 * Resolve effective header/footer HTML for a logical page number.
 * Purity: pure.
 */
const getHeaderFooterContentForPage = (
  type,
  pageNumber,
  documentData,
  sections,
  editors
) => {
  // Returns the effective HTML for one logical page number.
  // It is intentionally pure lookup logic (no DOM reads/writes).
  // IMPORTANT: pageNumber is document-global (1-based), not section-relative.
  if (!documentData?.record) return null;

  const fieldName = `${type}s`;
  const headerFooterConfigs = documentData.record[fieldName];

  if (!headerFooterConfigs || !Array.isArray(headerFooterConfigs)) return null;

  // Legacy format: direct page-range lookup.
  if (headerFooterConfigs[0]?.startPage !== undefined) {
    // Legacy payload: each entry owns an absolute page range.
    const applicableRange = headerFooterConfigs.find((range) => {
      return pageNumber >= range.startPage && pageNumber <= range.endPage;
    });
    return applicableRange?.html || null;
  }

  // New format: resolve section by cumulative page offset, then apply override.
  if (!sections || !editors) return null;

  let currentPageOffset = 0;
  for (const section of sections) {
    const sectionEditor = editors[section.id];
    const sectionNumPages = sectionEditor?.storage?.sectionInfo?.numPages || 1;
    const sectionStartPage = currentPageOffset + 1;
    const sectionEndPage = currentPageOffset + sectionNumPages;

    if (pageNumber >= sectionStartPage && pageNumber <= sectionEndPage) {
      const sectionConfig = headerFooterConfigs.find(
        (config) => config.sectionId === section.id
      );

      if (!sectionConfig) return null;

      const sectionRelativePage = pageNumber - currentPageOffset;
      // New payload: section base html can be overridden by section-relative
      // page ranges; first matching override wins.

      if (sectionConfig.overrides && sectionConfig.overrides.length > 0) {
        const applicableOverride = sectionConfig.overrides.find((override) => {
          const startMatch = sectionRelativePage >= override.startPage;
          const endMatch =
            override.endPage === NO_END_PAGE ||
            sectionRelativePage <= override.endPage;
          return startMatch && endMatch;
        });

        if (applicableOverride) {
          return applicableOverride.html || null;
        }
      }

      return sectionConfig.html || null;
    }

    currentPageOffset += sectionNumPages;
  }

  return null;
};

/**
 * Get maximum header height across section pages.
 * Purity: impure (reads cache + may measure DOM via `measureHeaderFooterHeight`).
 *
 * We intentionally use max-per-section so page spacing stays stable even when
 * per-page header overrides have different heights.
 */
const getMaxHeaderHeightForSection = (
  documentData,
  pageOffset,
  numPages,
  sectionId = null,
  sections = null,
  editors = null
) => {
  if (!hasDocumentHeaders(documentData)) return 0;

  const headers = documentData.record.headers;
  let maxHeight = 0;

  // Legacy format: include any range that overlaps this section page window.
  if (headers[0]?.startPage !== undefined) {
    const sectionStartPage = pageOffset + 1;
    const sectionEndPage = pageOffset + numPages;

    headers.forEach((header) => {
      if (!header.html || !header.html.trim()) return;

      const rangeStart = header.startPage;
      const rangeEnd = header.endPage;

      if (rangeEnd >= sectionStartPage && rangeStart <= sectionEndPage) {
        const height = measureHeaderFooterHeight(header.html, 'header');
        maxHeight = Math.max(maxHeight, height);
      }
    });
  } else if (sectionId && sections && editors) {
    // New format: include base section HTML + all override HTML candidates.
    const sectionConfig = headers.find((h) => h.sectionId === sectionId);

    if (sectionConfig) {
      if (sectionConfig.html && sectionConfig.html.trim()) {
        const height = measureHeaderFooterHeight(sectionConfig.html, 'header');
        maxHeight = Math.max(maxHeight, height);
      }

      if (sectionConfig.overrides && sectionConfig.overrides.length > 0) {
        sectionConfig.overrides.forEach((override) => {
          if (override.html && override.html.trim()) {
            const height = measureHeaderFooterHeight(override.html, 'header');
            maxHeight = Math.max(maxHeight, height);
          }
        });
      }
    }
  }

  return maxHeight;
};

/**
 * Get maximum footer height across section pages.
 * Purity: impure (reads cache + may measure DOM via `measureHeaderFooterHeight`).
 *
 * Same strategy as headers: compute maximum footer height for stable section
 * spacing regardless of per-page override variance.
 */
const getMaxFooterHeightForSection = (
  documentData,
  pageOffset,
  numPages,
  sectionId = null,
  sections = null,
  editors = null
) => {
  if (!hasDocumentFooters(documentData)) return 0;

  const footers = documentData.record.footers;
  let maxHeight = 0;

  // Legacy format: include any range that overlaps this section page window.
  if (footers[0]?.startPage !== undefined) {
    const sectionStartPage = pageOffset + 1;
    const sectionEndPage = pageOffset + numPages;

    footers.forEach((footer) => {
      if (!footer.html || !footer.html.trim()) return;

      const rangeStart = footer.startPage;
      const rangeEnd = footer.endPage;

      if (rangeEnd >= sectionStartPage && rangeStart <= sectionEndPage) {
        const height = measureHeaderFooterHeight(footer.html, 'footer');
        maxHeight = Math.max(maxHeight, height);
      }
    });
  } else if (sectionId && sections && editors) {
    // New format: include base section HTML + all override HTML candidates.
    const sectionConfig = footers.find((f) => f.sectionId === sectionId);

    if (sectionConfig) {
      if (sectionConfig.html && sectionConfig.html.trim()) {
        const height = measureHeaderFooterHeight(sectionConfig.html, 'footer');
        maxHeight = Math.max(maxHeight, height);
      }

      if (sectionConfig.overrides && sectionConfig.overrides.length > 0) {
        sectionConfig.overrides.forEach((override) => {
          if (override.html && override.html.trim()) {
            const height = measureHeaderFooterHeight(override.html, 'footer');
            maxHeight = Math.max(maxHeight, height);
          }
        });
      }
    }
  }

  return maxHeight;
};

/**
 * Convert measured content height into required page count.
 * Purity: pure.
 */
export const calculateNeededPages = (contentHeight) => {
  if (contentHeight <= 0) return 1;
  const effectiveHeight = Math.max(
    0,
    contentHeight - PAGINATION_REFLOW_EPSILON_PX
  );

  return Math.max(1, Math.ceil(effectiveHeight / PAGE_STRIDE));
};

/**
 * Build top-level node -> page index assignment for reflow detection.
 * Purity: impure (reads DOM geometry).
 */
const buildSectionPageAssignment = (sectionEl, tiptapEl) => {
  if (!sectionEl || !tiptapEl) return new Map();
  const sectionRect = sectionEl.getBoundingClientRect();
  const assignment = new Map();
  // Assignment keys are used only for reflow-detection telemetry/retry gates.
  // Use the shared node key format so snapshot + engine telemetry stay aligned.
  Array.from(tiptapEl.children).forEach((el, idx) => {
    const rect = el.getBoundingClientRect();
    if (!rect.height) return;
    const relTop = rect.top - sectionRect.top;
    const pageIdx = Math.max(
      0,
      Math.floor((relTop + PAGE_INDEX_EPSILON_PX) / PAGE_STRIDE)
    );
    const key = getElementStableKey(el, idx);
    // Key stability matters for retry telemetry: we compare page movement
    // across passes, not semantic identity across document sessions.
    assignment.set(key, pageIdx);
  });
  return assignment;
};

/**
 * Run full pagination pass for a section and apply page-break DOM.
 * Purity: impure (reads/writes DOM, uses timers, logs telemetry).
 *
 * Parameter grouping (for readability):
 * - Refs/context: `editorRef`, `pagesRef`
 * - Pagination state: `retryCount`, `pageOffset`, `pageNumbers`
 * - Section/document data: `documentHeaders`, `documentData`, `sections`, `editors`, `sectionId`
 * - Diagnostics/debug: `triggerReason`, `debugFlags`, `logger`
 *
 * Execution timeline (single section):
 * A) Sync debug root classes from the current debug-flags snapshot
 * B) Measure raw content height and initial page assignment
 * C) Compute needed pages and section CSS sizing vars
 * D) Render page/page-break DOM
 * E) Apply widow/orphan visual controls (may shift effective boundaries)
 * F) Re-measure for reflow deltas
 * G) Optionally retry (bounded) if boundaries moved materially
 */
export const paginateDocument = (
  editorRef,
  pagesRef,
  retryCount = 0,
  pageOffset = 0,
  documentHeaders = false,
  documentData = null,
  pageNumbers = null,
  sections = null,
  editors = null,
  sectionId = null,
  triggerReason = 'unknown',
  debugFlags = null,
  logger = null
) => {
  if (!editorRef.current || !pagesRef.current) return;
  // Keep telemetry + debug visuals on one snapshot source for this pass.
  const telemetryEnabled = Boolean(debugFlags?.pagination?.telemetryLogs);
  syncWoAdjustDebugRootClass(debugFlags);

  const editorContent = editorRef.current.querySelector('.tiptap');

  // Force recalculation of scroll height
  editorContent.style.height = 'auto';
  const initialContentHeight = editorContent.scrollHeight;
  const initialAssignment = buildSectionPageAssignment(
    editorRef.current,
    editorContent
  );

  const pageMargin = getPageMargin();
  const hasHeadersFooters = hasAnyHeadersOrFooters(documentData);
  // Abbrev is local-only; persisted settings key remains
  // `settings.pagination.widowOrphanControlEnabled`.
  const widOrphCtrlEnabled = Boolean(
    documentData?.settings?.pagination?.widowOrphanControlEnabled ??
      documentData?.record?.settings?.pagination?.widowOrphanControlEnabled
  );

  let neededPages = calculateNeededPages(initialContentHeight);

  /**
   * Apply explicit section/editor content heights for a target page count.
   * Purity: impure (mutates DOM inline styles).
   */
  const applySectionHeightForPages = (pageCount) => {
    // Section container height includes all page blocks plus inter-page dividers.
    // During print cycle, divider layers are visually removed, so we subtract
    // their contribution here to keep DOM height aligned with print layout.
    const pageContentHeight = pageCount * PAGE_HEIGHT;
    const dividerHeightTotal = PAGE_DIVIDER_HEIGHT * Math.max(0, pageCount - 1);
    const baseHeight = pageContentHeight + dividerHeightTotal;
    const middleLayersHidden = pageCount > 1 ? pageCount - 1 : 0;
    const totalHeight = isInPrintCycle
      ? baseHeight - middleLayersHidden * PAGE_DIVIDER_HEIGHT
      : baseHeight;
    // Keep section + tiptap heights in lockstep so scrollHeight/assignment
    // reads are computed from one consistent block size.
    const newHeight = `${totalHeight}px`;
    editorRef.current.style.height = newHeight;
    editorContent.style.height = newHeight;
  };

  /**
   * Rebuild page/page-break DOM for a section.
   * Purity: impure (mutates DOM nodes/HTML).
   */
  const renderPageBreakDom = (pageCount) => {
    // Full re-render each pass keeps page-index ordering deterministic and avoids
    // stale breaker attributes/layers after page-count changes.
    pagesRef.current.innerHTML = '';
    for (let i = 0; i < pageCount; i++) {
      if (i === 0 && hasHeadersFooters) {
        createFirstPageHeader(
          pagesRef,
          documentData,
          documentHeaders,
          pageOffset,
          sections,
          editors
        );
      }

      const page = document.createElement('div');
      page.classList.add('page');
      pagesRef.current.appendChild(page);

      const breaker = createPageBreak(
        i,
        pageCount,
        pageOffset,
        documentData,
        pageNumbers,
        sections,
        editors
      );
      // Each page always gets one breaker node so downstream WO/snapshot code
      // can rely on page-index continuity.
      pagesRef.current.appendChild(breaker);
    }
  };

  /**
   * Apply section height, page-break DOM, and WO controls for a target page count.
   * Purity: impure (mutates DOM styles/nodes and reads layout in WO pipeline).
   */
  const applyPageLayoutForCount = (pageCount) => {
    applySectionHeightForPages(pageCount);
    renderPageBreakDom(pageCount);
    applyEditorWidowOrphanVisualControls({
      editorRef,
      editorContent,
      pagesRef,
      neededPages: pageCount,
      getPageMargin,
      // Keep editor WO fallback aligned with snapshot producer fallback.
      widowOrphanMinLinesFallback: widOrphCtrlEnabled ? 2 : 1,
      pageIndexEpsilonPx: PAGE_INDEX_EPSILON_PX,
    });
  };

  let maxHeaderHeight = 0;
  let maxFooterHeight = 0;

  if (hasHeadersFooters) {
    maxHeaderHeight = getMaxHeaderHeightForSection(
      documentData,
      pageOffset,
      neededPages,
      sectionId,
      sections,
      editors
    );
    maxFooterHeight = getMaxFooterHeightForSection(
      documentData,
      pageOffset,
      neededPages,
      sectionId,
      sections,
      editors
    );

    // Section-scoped CSS vars feed `.page` spacer formulas and page-break layers.
    // We use max values across this section to keep within-section spacing stable
    // even when page-level header/footer content varies by override.
    const headerHeightWithPadding = maxHeaderHeight + pageMargin;
    const footerHeightWithPadding = maxFooterHeight + pageMargin;

    editorRef.current.style.setProperty(
      '--page-header-height',
      `${headerHeightWithPadding}px`
    );
    editorRef.current.style.setProperty(
      '--page-footer-height',
      `${footerHeightWithPadding}px`
    );
  } else {
    // No explicit header/footer content: default writable margins only.
    editorRef.current.style.setProperty(
      '--page-header-height',
      `${pageMargin}px`
    );
    editorRef.current.style.setProperty(
      '--page-footer-height',
      `${pageMargin}px`
    );
  }

  applyPageLayoutForCount(neededPages);

  // Retry if the content height changed after pagination
  // Skip retries during printing to avoid interfering with height adjustments
  const appliedEditorHeight = editorContent.style.height;
  editorContent.style.height = 'auto';
  const finalContentHeight = editorContent.scrollHeight;
  let finalNeededPages = calculateNeededPages(finalContentHeight);
  let finalAssignment = buildSectionPageAssignment(
    editorRef.current,
    editorContent
  );

  if (!isInPrintCycle && finalNeededPages !== neededPages) {
    // If WO or header/footer application changed effective layout enough to alter
    // page count, re-run once immediately with updated count before retry logic.
    neededPages = finalNeededPages;
    applyPageLayoutForCount(neededPages);
    editorContent.style.height = 'auto';
    finalNeededPages = calculateNeededPages(editorContent.scrollHeight);
    finalAssignment = buildSectionPageAssignment(
      editorRef.current,
      editorContent
    );
  }

  editorContent.style.height = appliedEditorHeight;
  const reflowDeltaPx = finalContentHeight - initialContentHeight;
  const pageCountDelta = finalNeededPages - neededPages;
  const movedNodes = [];
  initialAssignment.forEach((startPageIdx, key) => {
    const endPageIdx = finalAssignment.get(key);
    if (
      Number.isInteger(endPageIdx) &&
      Number.isInteger(startPageIdx) &&
      endPageIdx !== startPageIdx
    ) {
      movedNodes.push({key, from: startPageIdx, to: endPageIdx});
    }
  });
  const boundaryChanged = pageCountDelta !== 0 || movedNodes.length > 0;

  if (
    telemetryEnabled &&
    !isInPrintCycle &&
    retryCount > 0 &&
    boundaryChanged
  ) {
    (logger?.warn || console.warn)(
      '[pagination] Reflow changed page boundaries',
      {
        sectionId: sectionId || null,
        triggerReason,
        retryCount,
        neededPages,
        finalNeededPages,
        pageCountDelta,
        movedNodeCount: movedNodes.length,
        movedNodeSample: movedNodes.slice(0, 8),
        initialContentHeight,
        finalContentHeight,
        reflowDeltaPx,
        epsilonPx: PAGINATION_REFLOW_EPSILON_PX,
      }
    );
  }
  // Retry decision path:
  // 1) never retry during print cycle
  // 2) cap retries (max 3) to guarantee convergence
  // 3) require meaningful height delta (> epsilon)
  // 4) require boundary movement (page count or assignment changed)
  // 5) skip churn case when WO boost marker is present and page count is stable
  const shouldRetryForReflow =
    !isInPrintCycle &&
    retryCount < 3 &&
    Math.abs(reflowDeltaPx) > PAGINATION_REFLOW_EPSILON_PX &&
    boundaryChanged &&
    !(
      pagesRef.current?.querySelector?.(
        `[${EDITOR_WO_PAGE_FOOTER_BOOST_ATTR}="1"]`
      ) && pageCountDelta === 0
    );
  // Guardrail: when WO boost is active and page count is unchanged, avoid
  // retry churn from tiny local geometry shifts that already converged.

  if (shouldRetryForReflow) {
    // Bounded async retry lets layout settle after DOM writes and avoids tight
    // synchronous loops in the same frame.
    setTimeout(
      () =>
        paginateDocument(
          editorRef,
          pagesRef,
          retryCount + 1,
          pageOffset,
          documentHeaders,
          documentData,
          pageNumbers,
          sections,
          editors,
          sectionId,
          triggerReason,
          debugFlags,
          logger
        ),
      100
    );
  }
  return neededPages;
};

// ---------------------------------------------------------------------------
// PAGE-BREAK DOM BUILDERS
// ---------------------------------------------------------------------------
// These helpers build the synthetic page-break DOM consumed by editor view,
// WO adjustments, and snapshot measurement logic.

/**
 * Create one page-break DOM node (footer/current-page + optional next header).
 * Purity: impure (creates/mutates DOM nodes).
 */
const createPageBreak = (
  index,
  totalPages,
  pageOffset = 0,
  documentData = null,
  pageNumbers = null,
  sections = null,
  editors = null
) => {
  const breaker = document.createElement('div');
  breaker.classList.add(PAGE_BREAK_CLASS);
  breaker.setAttribute(PAGE_BREAK_PAGE_INDEX_ATTR, `${index}`);

  const currentPageNum = pageOffset + index + 1;
  const nextPageNum = pageOffset + index + 2;
  const isLastPage = index === totalPages - 1;

  const footerHtml = getHeaderFooterContentForPage(
    'footer',
    currentPageNum,
    documentData,
    sections,
    editors
  );
  // `nextHeaderHtml` renders in this breaker's bottom layer so each breaker
  // visually models "footer of current page + header of next page".
  const nextHeaderHtml = !isLastPage
    ? getHeaderFooterContentForPage(
        'header',
        nextPageNum,
        documentData,
        sections,
        editors
      )
    : null;

  const topLayer = document.createElement('div');
  topLayer.classList.add(PAGE_BREAK_TOP_CLASS);

  const footerContent = document.createElement('div');
  footerContent.classList.add('header-footer-content');
  if (footerHtml) {
    // Sanitize HTML before DOM injection for defense-in-depth
    footerContent.innerHTML = sanitizeHeaderFooterHtml(footerHtml);
  }
  topLayer.appendChild(footerContent);

  const pageNumberDiv = document.createElement('div');
  pageNumberDiv.classList.add('page-number');
  const pageNumber = pageNumbers ? pageNumbers[pageOffset + index] || '' : '';
  pageNumberDiv.textContent = pageNumber;
  topLayer.appendChild(pageNumberDiv);

  // Preset HTML can carry data-parent-order on its root element to control
  // where it sits relative to siblings in .page-break-top (e.g. after page number).
  // We bubble it up to .header-footer-content since that's the flex child.
  const parentOrder = footerContent.querySelector('[data-parent-order]')
    ?.dataset?.parentOrder;
  if (parentOrder) {
    footerContent.style.order = parentOrder;
    topLayer.style.justifyContent = 'flex-end';
  }

  ensureWoAdjustLayer(breaker);
  breaker.appendChild(topLayer);

  // Only add middle and bottom layers if there's a next page
  if (!isLastPage) {
    const middleLayer = document.createElement('div');
    middleLayer.classList.add('page-break-middle');
    breaker.appendChild(middleLayer);

    const bottomLayer = document.createElement('div');
    bottomLayer.classList.add(PAGE_BREAK_BOTTOM_CLASS);

    if (nextHeaderHtml) {
      const headerContent = document.createElement('div');
      headerContent.classList.add('header-footer-content');
      // Sanitize HTML before DOM injection for defense-in-depth
      headerContent.innerHTML = sanitizeHeaderFooterHtml(nextHeaderHtml);
      bottomLayer.appendChild(headerContent);
    }

    breaker.appendChild(bottomLayer);
  }

  return breaker;
};

/**
 * Inject first-page header block before page sequence when enabled.
 * Purity: impure (creates/appends DOM nodes).
 */
const createFirstPageHeader = (
  pagesRef,
  documentData = null,
  documentHeadersEnabled,
  pageOffset = 0,
  sections = null,
  editors = null
) => {
  if (!documentHeadersEnabled) return;

  if (!hasDocumentHeaders(documentData) && !hasDocumentFooters(documentData)) {
    return;
  }

  const firstPageNum = pageOffset + 1;
  const headerHtml = getHeaderFooterContentForPage(
    'header',
    firstPageNum,
    documentData,
    sections,
    editors
  );

  const firstPageHeader = document.createElement('div');
  firstPageHeader.classList.add(PAGE_BREAK_CLASS);

  const headerWrapper = document.createElement('div');
  headerWrapper.classList.add(PAGE_BREAK_BOTTOM_CLASS);

  if (headerHtml) {
    const headerContent = document.createElement('div');
    headerContent.classList.add('header-footer-content');
    // Sanitize HTML before DOM injection for defense-in-depth
    headerContent.innerHTML = sanitizeHeaderFooterHtml(headerHtml);
    headerWrapper.appendChild(headerContent);
  }

  firstPageHeader.appendChild(headerWrapper);
  // Insert before normal page/breaker sequence to represent page-1 header
  // without requiring a "previous page" breaker.
  pagesRef.current.appendChild(firstPageHeader);
};

/**
 * Build document page-number labels from section rules.
 * Purity: pure.
 *
 * Rule model:
 * - A rule starts at its section and continues until the next section rule.
 * - Numbering state (`pagesInCurrentRule`) resets when a new rule starts.
 * - `showPageNumber=false` still advances internal counters so later rules align.
 */
export const generatePageNumbers = (
  pageNumberRules,
  sections,
  sectionPageCount
) => {
  const totalPageCount = sections.reduce(
    (acc, section) => acc + (sectionPageCount[section.id] || 0),
    0
  );

  const documentPageNumbers = Array.from({length: totalPageCount}, () => '');

  let sectionPageOffset = 0;
  let currentRule = null;
  // Tracks the actual displayed page number, persists across rules for continuous mode
  let runningPageNumber = 0;
  // True only for the section that explicitly triggered the current rule (not inherited sections)
  let isRuleStartSection = false;

  if (!pageNumberRules || pageNumberRules.length === 0) {
    pageNumberRules = [
      {
        sectionId: sections[0]?.id || '',
        showPageNumber: true,
        startNumber: 1,
        prefix: '',
      },
    ];
  }

  for (const section of sections) {
    const currentSectionPages = sectionPageCount[section.id] || 0;
    const rule = pageNumberRules.find((r) => r.sectionId === section.id);

    if (rule) {
      currentRule = rule;
      isRuleStartSection = true;
      // Continuous mode: keep runningPageNumber as-is so numbering picks up where the last section left off.
      // Set-number mode (including old rules without continuousFromPrevious): reset to startNumber.
      if (!rule.continuousFromPrevious) {
        runningPageNumber = (rule.startNumber || 1) - 1;
      }
    } else {
      isRuleStartSection = false;
    }

    if (currentRule) {
      const prefix = currentRule.prefix || '';
      const firstPageDisplay = currentRule.firstPageDisplay ?? 'show';
      // When showPageNumber is false and sectionCountMode is 'exclude', pages in
      // this section don't advance the running total (backward compat: undefined → 'include')
      const excludeSectionFromCount =
        !currentRule.showPageNumber &&
        currentRule.sectionCountMode === 'exclude';

      for (let p = 0; p < currentSectionPages; p++) {
        const pageIndex = sectionPageOffset + p;
        const isFirstPageOfRuleSection = isRuleStartSection && p === 0;

        // 'hide_exclude': first page is not counted in the running total.
        // Only applies when showPageNumber is true — when hidden, sectionCountMode
        // governs whether pages count, so firstPageDisplay is irrelevant.
        if (
          currentRule.showPageNumber &&
          firstPageDisplay === 'hide_exclude' &&
          isFirstPageOfRuleSection
        ) {
          documentPageNumbers[pageIndex] = '';
          continue;
        }

        if (!excludeSectionFromCount) {
          runningPageNumber++;
        }

        const hideDisplay =
          firstPageDisplay !== 'show' && isFirstPageOfRuleSection;

        if (currentRule.showPageNumber && !hideDisplay) {
          documentPageNumbers[pageIndex] = `${prefix}${runningPageNumber}`;
        } else {
          documentPageNumbers[pageIndex] = '';
        }
      }
    }

    sectionPageOffset += currentSectionPages;
  }

  return documentPageNumbers;
};
